<template>
  <section>
    <configurator v-for="related in product.related[type]"
                  v-if="related.custom_options && related.custom_options.length > 0"
                  :product="related"
                  :key="related.sku"
                  :category-id="closestCategory ? closestCategory.category_id : 0"
    />
    <section
      class="py20 new-collection container px15"
    >
      <div>
        <header class="col-md-12">
          <h4 class="align-left cl-accent">
            {{ heading }} <router-link :to="category" class="name-category">{{ categoryProduct }}</router-link>
          </h4>
        </header>
      </div>
      <div class="related-listing-row row center-xs">
        <product-listing-related
          v-if="product.related[type] && product.related[type].length > 0"
          columns="4"
          :products="product.related[type]"
        />
        <product-listing-skeleton v-else />
      </div>
    </section>
  </section>
</template>

<script async>
import i18n from '@vue-storefront/i18n';
import config from 'config';
import { prepareRelatedQuery } from '@vue-storefront/core/modules/catalog/queries/related';
import { mapGetters, mapActions } from 'vuex';
import ProductListingSkeleton from 'theme/components/core/ProductListingSkeleton.vue';

const Configurator = () => import(/* webpackChunkName: "vsf-configurator" */ 'theme/components/theme/blocks/Home/ProductTileGrid/Configurator');
const ProductListingRelated = () => import(/* webpackChunkName: "vsf-product-listing-related" */ 'theme/components/core/ProductListingRelated.vue');

export default {
  name: 'Related',
  props: {
    type: {
      type: String,
      required: true
    },
    heading: {
      type: String,
      required: false,
      default: i18n.t('More from the section')
    }
  },
  components: {
    ProductListingRelated,
    ProductListingSkeleton,
    Configurator
  },
  data: () => ({
    relatedCategory: null
  }),
  watch: {
    categoryProduct: {
      deep: true,
      handler: function () {
        this.refreshList()
      }
    }
  },
  beforeMount () {
    this.$bus.$on('product-after-load', this.refreshList)

    if (config.usePriceTiers) {
      this.$bus.$on('user-after-loggedin', this.refreshList)
      this.$bus.$on('user-after-logout', this.refreshList)
    }

    this.refreshList()
  },
  beforeDestroy () {
    if (config.usePriceTiers) {
      this.$bus.$off('user-after-loggedin', this.refreshList)
      this.$bus.$off('user-after-logout', this.refreshList)
    }
  },
  destroyed () {
    this.$bus.$off('product-after-load', this.refreshList)
    this.$store.dispatch('product/related', {
      key: this.type,
      items: []
    })
  },
  methods: {
    ...mapActions({
      loadCategory: 'category/single'
    }),
    refreshList () {
      if (this.product.current.partner_code && this.product.current.partner_code !== this.currentPartner.code) {
        this.$store.dispatch('partners/changeCurrentPartner', this.product.current.partner_code)
      }
      let sku = this.productLinks ? this.productLinks
        .filter(pl => pl.link_type === this.type)
        .map(pl => pl.linked_product_sku) : null
      let key = 'sku'
      if (sku === null || (sku.length === 0)) {
        if (this.currentPartner && this.currentPartner.display_mode === 'shop') {
          if (this.closestCategory) {
            sku = this.closestCategory.category_id
          } else {
            sku = this.currentPartner.root_category_id
          }
        } else if (this.product.current.category_ids && this.product.current.category_ids.length) {
          sku = this.product.current.category_ids
        }
        key = 'category_ids'
      }
      let relatedProductsQuery = prepareRelatedQuery(key, sku)

      this.$store.dispatch('product/list', {
        query: relatedProductsQuery,
        size: 8,
        prefetchGroupProducts: false,
        updateState: false
      }).then((response) => {
        if (response) {
          this.$store.dispatch('product/related', {
            key: this.type,
            items: response.items
          })
          this.$forceUpdate()
        }
      })

      this.loadCategory({
        key: 'entity_id',
        value: this.closestCategory.category_id,
        partner: this.getCurrentPartnerCode,
        setCurrentCategory: false,
        setCurrentCategoryPath: false,
        updateState: false
      })
        .then(res => {
          if (res) {
            this.relatedCategory = res
          }
        })
    }
  },
  computed: {
    ...mapGetters({
      currentLocation: 'locations/getActiveLocation',
      currentPartner: 'partners/getCurrentPartner',
      getCurrentPartnerCode: 'partners/getCurrentPartnerCode',
      getCurrentPartnerDisplayMode: 'partners/getCurrentPartnerDisplayMode',
    }),
    categoriesAmount () {
      return this.product.current.category_ids.length
    },
    closestCategory () {
      let category = typeof this.product.current.category === 'object' ? this.product.current.category : this.getCategories
      if (this.categoriesAmount) {
        let category_id = this.product.current.category_ids[this.categoriesAmount - 1]
        return category.find(category => category.category_id || category.entity_id === category_id)
      }
      // TODO: Load root category
      return {}
    },
    products () {
      return this.product.related[this.type]
    },
    product () {
      return this.$store.state.product
    },
    productLinks () {
      return this.product.current.product_links
    },
    category () {
      return (this.getCurrentPartnerDisplayMode !== 'food')
        ? `/${this.currentLocation.code}/${this.getCurrentPartnerCode}/${this.urlCategory}`
        : `/${this.currentLocation.code}/${this.getCurrentPartnerCode}?section=${this.urlCategory}`
    },
    categoryProduct () {
      return this.closestCategory.name;
    },
    urlCategory () {
      if (!this.closestCategory.url_path && this.$store.state.category.current_path.length > 0) {
        let category_id = this.closestCategory.entity_id || this.product.current.category_ids[this.categoriesAmount - 1]
        let category = this.getCategories.find(category => category.category_id || category.entity_id === category_id)
        if (category) {
          return category.url_path
        }
      }
      if (this.relatedCategory) {
        return this.relatedCategory.url_path
      }
      return this.closestCategory.url_path
    }
  }
}
</script>

<style scoped lang="scss">
.related-listing-row {
  min-height: 365px;

  @media only screen and (max-width: 991px) {
    min-height: 310px;
  }

  @media only screen and (max-width: 768px) {
    min-height: 240px;
  }

  @media only screen and (max-width: 500px) {
    min-height: auto;
  }
}

.name-category{
  color: #11bb2e;
  text-transform: lowercase;
  border-bottom: 1px solid #11bb2e;
}

::v-deep .product-listing-skeleton {
  &__root {
    padding: 0 8px;
    column-gap: 20px;
  }

  &__item {
    width: 225px;
    height: 365px;

    .placeholder {
      transform: scale(0.75);
    }

    @media only screen and (max-width: 991px) {
      height: 310px;
    }

    @media only screen and (max-width: 768px) {
      height: 240px;
    }

    @media only screen and (max-width: 500px) {
      height: auto;

      &:first-of-type, &:last-of-type {
        display: none;
      }
    }
  }
}
</style>
